import { tokens } from "lib/colors"
import { gtmEvent } from "lib/gtm"
import { colorType } from "lib/interfaces"
import Icons from "lib/components/Icons"
import Text, { weightProps } from "lib/components/Text"
import Link from "next/link"
import { RefObject, useContext, useMemo, useRef, useState } from "react"
import useGhostTags from "lib/hooks/useGhostTags"
import { LanguageContext } from "context/LanguageContext"

type ProgramCategories = {
  id: any
  category_name: string
  slug?: string
}

type CategoriesQuery = {
  parent_id: any
  slug?: string
  priority: number
  category_name: string
  category_description?: string
  id: any

  categories: ProgramCategories[]
}

interface CloseDropdownFunction {
  elementRef: RefObject<HTMLDivElement>
  isDropdownOpen: boolean
}

export type keySelectCategory = "category_name" | "category_name_indo"
export interface CategorySelectorProps {
  color?: colorType
  weight?: weightProps
  isHeader?: boolean
  categories?: CategoriesQuery[]
  language: "ID" | "EN"
  title?: string
  topCategories?: string
  allCategory?: string
  setActiveDropdown
  activeDropdown
  showMarquee?: boolean
}

function CategorySelectorBlog({
  isHeader = false,
  color = "light",
  weight = "normal",
  categories = [],
  language = "ID",
  title = "Training",
  topCategories = "Top Categories",
  allCategory = "All Categories",
  setActiveDropdown,
  activeDropdown,
  showMarquee,
}: CategorySelectorProps) {
  const { useTranslation: t } = useContext(LanguageContext)
  const categoryBlog = useGhostTags()
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [keySelectedCategory, setKeySelectedCategory] = useState<keySelectCategory>("category_name")
  const categoryData = categories.filter((category) => !category.parent_id)
  const subCategoryData = categories.filter((category) => category.parent_id)
  const priorityCategoryData = subCategoryData.sort((a, b) => b.priority - a.priority)
  const topCategoryData = priorityCategoryData.slice(0, 10)

  const selectedSubCategoryData =
    categories.find((category) => category.id === selectedCategory)?.categories || []

  const refCategory: any = useRef()
  let animationType = "opacity-0"
  let animationIcon = "transform rotate-0 duration-200"
  let hexColor = isHeader ? tokens.neutral[10] : "#000"

  if (activeDropdown === "category") {
    animationType = "opacity-100"
    animationIcon = "transform rotate-180 duration-200"
  }

  if (activeDropdown !== "category") {
    animationType = "hidden"
  }

  switch (color) {
    case "primary":
      hexColor = tokens.primary.DEFAULT
      break
    case "secondary":
      hexColor = tokens.secondary.DEFAULT
      break
    default:
      break
  }

  useMemo(() => {
    setKeySelectedCategory("category_name")
    if (language === "ID") {
      setKeySelectedCategory("category_name_indo")
    }
  }, [language])

  const handleSelectCategory = (categoryId) => {
    setSelectedCategory(categoryId)
  }

  const closeDropdown = ({ elementRef, isDropdownOpen }: CloseDropdownFunction) => {
    // close if user click outside the dropdown and isDropdownOpen true
    if (!elementRef.current?.matches(":hover") && isDropdownOpen) {
      setActiveDropdown(null)
    }
  }

  return (
    <div className="relative">
      <div
        role="button"
        tabIndex={0}
        className={`flex flex-row gap-0 items-center ${isHeader ? "border-b-2 border-primary" : "border-b-2 border-transparent"} py-1.5 sm:py-3 sm:my-0`}
        onClick={() => {
          if (activeDropdown === "category") {
            setActiveDropdown(null)
          } else {
            setActiveDropdown("category")
          }
        }}
        onBlur={() =>
          closeDropdown({
            elementRef: refCategory,
            isDropdownOpen: activeDropdown === "category",
          })
        }>
        <Text weight={weight} customClass={`font-semibold ${isHeader ? "!text-primary" : "!text-neutral-50"} text-sm sm:text-base`}>
          {title}
        </Text>
        <Icons iconName="expand-more" styles={animationIcon} color={isHeader ? tokens.primary.DEFAULT : tokens.neutral[50]} />
      </div>
      <div
        ref={refCategory}
        className={`transform ease-in-out duration-500 ${animationType} absolute mx-auto inset-0 left-0 sm:-left-16 top-12 lg:top-8 mt-2 sm:mt-6 z-20 ${activeDropdown === "category" ? "hover:block" : "hidden"}`}
        onBlur={() =>
          closeDropdown({
            elementRef: refCategory,
            isDropdownOpen: activeDropdown === "category",
          })
        }>
        <div className="bg-white rounded-lg shadow-search flex flex-row h-[360px] w-[383px]">
          <div className="w-1/2 border-r-2 px-2 py-2 overflow-x-auto flex flex-col gap-y-2">
            <Link href="/blog?type=insight" passHref legacyBehavior>
              <div
                key="all"
                className="px-1.5 hover:px-2 py-2 rounded-md hover:bg-info-surface !text-neutral-50 hover:!text-black cursor-pointer"
                role="button"
                tabIndex={0}
                onMouseEnter={() => {
                  handleSelectCategory(null)
                }}
                onClick={() => {
                  setActiveDropdown("category")
                  handleSelectCategory(null)
                  gtmEvent("ce_header_menu_click", {
                    menu_name: "All Categories",
                  })
                }}>
                <Text>{allCategory}</Text>
              </div>
            </Link>
            {categoryData.map((category) => {
              let styles = ""
              if (selectedCategory === category.id) {
                styles = "bg-tag"
              }

              if (category.categories.length > 0) {
                return (
                  <div
                    key={category.id}
                    className={`grid grid-cols-6 items-center gap-x-2 px-1.5 hover:px-2 py-2 rounded-md hover:bg-info-surface !text-neutral-50 hover:!text-black cursor-pointer ${styles}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      gtmEvent("ce_header_menu_click", {
                        menu_name: category.category_name,
                      })
                      setActiveDropdown(null)
                      window.location.href = `/blog?type=insight&tag=${category.slug}`
                    }}
                    onMouseEnter={() => {
                      handleSelectCategory(category.id)
                    }}>
                    <Text customClass="col-span-5">
                      {category[keySelectedCategory] || category.category_name}
                    </Text>
                    <Icons iconName="arrow-right" width={24} height={24} />
                  </div>
                )
              }
              return (
                <Link
                  key={category.slug}
                  href={`/blog?type=insight&tag=${category.slug}`}
                  passHref
                  legacyBehavior>
                  <div
                    key="all"
                    className="px-1.5 hover:px-2 py-2 rounded-md hover:bg-info-surface !text-neutral-50 hover:!text-black cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onMouseEnter={() => {
                      handleSelectCategory(null)
                    }}
                    onClick={() => {
                      gtmEvent("ce_header_menu_click", {
                        menu_name: category.category_name,
                      })
                      setActiveDropdown(null)
                      handleSelectCategory(null)
                    }}>
                    <Text>
                      {category[keySelectedCategory] || category.category_name}
                    </Text>
                  </div>
                </Link>
              )
            })}
          </div>
          <div className="w-1/2 p-2 overflow-x-auto">
            <div className="h-fit flex flex-col">
              {selectedSubCategoryData.length > 0 ? (
                <>
                  <div>
                    <Text weight="semibold" size="extra-small">
                      {t("article_options")}
                    </Text>
                  </div>
                  {selectedSubCategoryData.map((item) => (
                    <a key={item.id} href={`/blog?type=insight&tag=${item.slug}`}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          gtmEvent("ce_header_menu_click", {
                            menu_name: item.category_name,
                          })
                          setActiveDropdown(null)
                          handleSelectCategory(null)
                        }}
                        className=" hover:bg-info-surface hover:px-1.5 cursor-pointer py-2 rounded-lg !text-neutral-50 hover:!text-black flex justify-between items-center">
                          <Text size="small" customClass="cursor-pointer">
                            {item[keySelectedCategory] || item.category_name}
                          </Text>
                          <Text size="small" customClass="mr-1">
                            {categoryBlog.find((cat) => cat.slug === item.slug)?.count?.posts || 0}
                          </Text>
                      </div>
                    </a>
                  ))}
                </>
              ) : (
                <div>
                  <div>
                    <Text weight="semibold" size="extra-small">
                      {topCategories}
                    </Text>
                  </div>
                  <div className="flex flex-col mt-1">
                    {topCategoryData.map((cat) => (
                      <a key={cat.slug} href={`/blog?type=insight&tag=${cat.slug}`}>
                        <div
                          className="hover:bg-info-surface py-2 hover:px-1.5 rounded-lg !text-neutral-50 hover:!text-black flex justify-between items-center"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            gtmEvent("ce_header_menu_click", {
                              menu_name: cat.category_name,
                            })
                            setActiveDropdown(null)
                            handleSelectCategory(null)
                          }}>
                            <div className="rounded-lg">
                              <Text size="small" customClass="cursor-pointer">
                                {cat[keySelectedCategory] || cat.category_name}
                              </Text>
                            </div>
                            <div>
                              <Text size="small" customClass="mr-1">
                                {categoryBlog.find((categoryValue) => categoryValue.slug === cat.slug)?.count?.posts || 0}
                              </Text>
                            </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CategorySelectorBlog
